// landing/FeatureShowcaseSection.jsx
import React from 'react';
import { 
  KeyRound, Brain, Folder, Pin, Puzzle, Search, Scissors, Code, BookMarked, Mail, Clock, Globe, Layers
} from 'lucide-react';

/* ========================
 * BetaTag - Small beta indicator component
 * For indicating features still in development
 * ========================*/
const BetaTag = ({ className = "" }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/10 text-orange-400 border border-orange-500/20 ${className}`}>
    BETA
  </span>
);

/* ========================
 * FeatureCard - Reusable component for feature cards in mobile view
 * For internal use within FeatureShowcaseSection
 * ========================*/
const FeatureCard = ({ icon: Icon, title, description, isBeta = false }) => (
  <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-lg p-4 flex items-start gap-4">
    <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-xl backdrop-blur-sm border border-orange-500/30 flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-orange-400" />
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-bold text-white text-sm">{title}</h3>
        {isBeta && <BetaTag />}
      </div>
      <p className="text-xs text-gray-300">{description}</p>
    </div>
  </div>
);

/* ========================
 * ConstellationFeature - Reusable component for feature cards in desktop constellation view
 * Handles icon, text, and positioning
 * ========================*/
const ConstellationFeature = ({ icon: Icon, title, description, isBeta = false, positionClasses, reverseLayout = false }) => (
  <div className={`absolute ${positionClasses}`}>
    <div className={`flex items-center ${reverseLayout ? 'flex-row-reverse' : ''}`}>
      <div className={`w-16 h-16 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-xl backdrop-blur-sm border border-orange-500/30 flex items-center justify-center z-10 ${reverseLayout ? '-ml-2' : '-mr-2'}`}>
        <Icon className="w-8 h-8 text-orange-400" />
      </div>
      <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-lg p-3 max-w-[220px]">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-bold text-white text-sm">{title}</h3>
          {isBeta && <BetaTag />}
        </div>
        <p className="text-xs text-gray-300">{description}</p>
      </div>
    </div>
  </div>
);

/* ========================
 * FeatureShowcaseSection - Displays the key features in a constellation layout
 * Highlights core functionalities of Shelbula
 * ========================*/
const FeatureShowcaseSection = () => {
  // Define main features for the constellation with their desktop positions and layout
  const mainFeatures = [
    {
      icon: KeyRound,
      title: "Plug In External APIs",
      description: "Connect OpenAI, Claude, Gemini, Mistral, Grok, and OpenRouter without locking yourself into one provider.",
      positionClasses: "top-32 left-32",
      reverseLayout: false,
    },
    {
      icon: Brain,
      title: "Workspace Memory",
      description: "Carry forward preferences, notes, and working context instead of starting over in every thread.",
      positionClasses: "top-32 right-32",
      reverseLayout: true,
    },
    {
      icon: Folder,
      title: "Private Project Workspaces", 
      description: "Give important projects their own isolated files, context, history, and working surface.",
      isBeta: true,
      positionClasses: "top-1/2 left-16 -translate-y-1/2",
      reverseLayout: false,
    },
    {
      icon: Puzzle,
      title: "External APIs + Tools",
      description: "Connect MCP servers and tool workflows so your assistant can do more than answer text.",
      positionClasses: "top-1/2 right-16 -translate-y-1/2",
      reverseLayout: true,
    },
    {
      icon: Search,
      title: "Live Research",
      description: "Search the web, inspect sites, and pull current information directly into the workspace.",
      positionClasses: "bottom-48 left-48",
      reverseLayout: false,
    },
    {
      icon: BookMarked,
      title: "Snippets, Notes, Context",
      description: "Keep reusable prompts, reference notes, and working context close enough for your assistant to use.",
      positionClasses: "bottom-48 right-48",
      reverseLayout: true,
    },
    {
      icon: Code,
      title: "Built to Work, Not Just Chat",
      description: "Code blocks, files, rewinds, pinned context, and iteration support for real ongoing work.",
      positionClasses: "bottom-24 left-1/2 -translate-x-1/2",
      isCentered: true, // New prop for special centering style
    }
  ];

  // Define additional automation features for bottom row
  const automationFeatures = [
    {
      icon: Layers,
      title: "Created Workspaces",
      description: "Spin up isolated environments when a project needs its own files, memory, and operating boundary."
    },
    {
      icon: Pin,
      title: "Pinned Context",
      description: "Keep important instructions and reference material available without repeating yourself."
    },
    {
      icon: Scissors,
      title: "Context Control",
      description: "Manage history intelligently so long-running work stays useful without wasting tokens."
    },
    {
      icon: Globe,
      title: "Website Exploration",
      description: "Scan, extract, and compare content across multiple sites without leaving the workspace."
    },
    {
      icon: Mail,
      title: "Assistant Email",
      description: "Let your workspace send useful updates, summaries, and outputs back to you."
    }
  ];

  // All features combined for mobile
  const allFeatures = [...mainFeatures, ...automationFeatures];

  return (
    <section id="features" className="relative py-2 px-6 border-t border-white/5 z-10">
      <div className="max-w-7xl mx-auto">
        <div className="relative">
          {/* Different layouts for mobile and desktop */}
          
          {/* DESKTOP LAYOUT - Constellation with central hub + bottom row */}
          <div className="hidden md:block relative min-h-[1000px]">
            
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(251,146,60,0.1)_0%,transparent_50%)]"></div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnL3MyMDAwL3N2ZyI+PHBhdGggZD0iNTkuNSA2MEgwVjBoNjBWNjBoLS41ek01OSAxSDFtMCAxaDU4TTEgM2g1OE0xIDRoNTgiIHN0cm9rZT0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==')] opacity-30"></div>
            </div>
            
            {/* Constellation container */}
            <div className="relative flex items-center justify-center" style={{ height: '800px' }}>
              
              {/* Central core */}
              <div className="relative z-10 text-center" style={{ marginTop: '-200px' }}>
                <div className="w-32 h-32 mx-auto mb-8 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full animate-pulse-slow"></div>
                  <div className="absolute inset-2 bg-gray-950 rounded-full flex items-center justify-center">
                    {/* Cat logo */}
                    <div className="relative w-16 h-16">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="w-16 h-16 text-orange-500 relative"
                      >
                        <path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3.1-9-7.56c0-1.2.43-2.37 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z"/>
                        <path d="M8 14v.5" />
                        <path d="M16 14v.5" />
                        <path d="M11.25 16.25h1.5L12 17l-.75-.75Z" />
                      </svg>
                    </div>
                  </div>
                  {/* Orbiting dots */}
                  <div className="absolute inset-0 animate-spin" style={{ animationDuration: '20s' }}>
                    <div className="absolute top-0 left-1/2 w-3 h-3 bg-orange-400 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 left-1/2 w-3 h-3 bg-amber-400 rounded-full -translate-x-1/2 translate-y-1/2"></div>
                    <div className="absolute left-0 top-1/2 w-3 h-3 bg-orange-300 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute right-0 top-1/2 w-3 h-3 bg-amber-300 rounded-full translate-x-1/2 -translate-y-1/2"></div>
                  </div>
                </div>
                
                <h2 className="mx-auto mb-4 max-w-[760px] text-4xl font-bold leading-[0.95] md:text-5xl bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                  <span className="block">Your AI-Enabled</span>
                  <span className="block">Private Workspace</span>
                </h2>
                <p className="mx-auto mb-2 max-w-[680px] text-base leading-relaxed text-gray-300 md:text-lg">
                  Chat, files, tools, memory, and external APIs in one working environment
                </p>
              </div>
              
              {/* Feature nodes arranged in a constellation */}
              <div className="absolute inset-0">
                {mainFeatures.map((feature, index) => (
                  <ConstellationFeature
                    key={index}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    isBeta={feature.isBeta}
                    positionClasses={feature.positionClasses}
                    reverseLayout={feature.reverseLayout}
                  />
                ))}
              </div>
            </div>

            {/* Bottom row of 3 automation features - positioned below constellation */}
            <div className="relative mt-0 mb-8">
              <div className="flex justify-center items-start gap-8 max-w-6xl mx-auto">
                {automationFeatures.map((feature, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-amber-500/20 rounded-xl backdrop-blur-sm border border-orange-500/30 flex items-center justify-center z-10 mb-2">
                      <feature.icon className="w-8 h-8 text-orange-400" />
                    </div>
                    <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-lg p-3 max-w-[220px] text-center -mt-4">
                      <h3 className="font-bold text-white text-sm mb-1">{feature.title}</h3>
                      <p className="text-xs text-gray-300">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* MOBILE LAYOUT - Completely separate from desktop layout */}
          <div className="md:hidden py-10">
            {/* Central logo & header for mobile */}
            <div className="relative z-10 text-center mb-10">
              <div className="w-24 h-24 mx-auto mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full animate-pulse-slow"></div>
                <div className="absolute inset-2 bg-gray-950 rounded-full flex items-center justify-center">
                  {/* Cat logo */}
                  <div className="relative w-12 h-12">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-12 h-12 text-orange-500 relative"
                    >
                      <path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3.1-9-7.56c0-1.2.43-2.37 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5Z"/>
                      <path d="M8 14v.5" />
                      <path d="M16 14v.5" />
                      <path d="M11.25 16.25h1.5L12 17l-.75-.75Z" />
                    </svg>
                  </div>
                </div>
                {/* Simplified orbiting dots for mobile */}
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '20s' }}>
                  <div className="absolute top-0 left-1/2 w-2 h-2 bg-orange-400 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                  <div className="absolute bottom-0 left-1/2 w-2 h-2 bg-amber-400 rounded-full -translate-x-1/2 translate-y-1/2"></div>
                  <div className="absolute left-0 top-1/2 w-2 h-2 bg-orange-300 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                  <div className="absolute right-0 top-1/2 w-2 h-2 bg-amber-300 rounded-full translate-x-1/2 -translate-y-1/2"></div>
                </div>
              </div>
              
              <h2 className="text-3xl font-bold leading-tight mb-3 bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                <span className="block">Your AI-Enabled</span>
                <span className="block">Private Workspace</span>
              </h2>
              <p className="mx-auto mb-4 max-w-[22rem] text-base leading-relaxed text-gray-300">
                Chat, files, tools, memory, and external APIs in one working environment
              </p>
            </div>

            {/* Stacked feature cards for mobile - includes all features */}
            <div className="space-y-4 mb-10">
              {allFeatures.map((feature, index) => (
                <FeatureCard 
                  key={index}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  isBeta={feature.isBeta}
                />
              ))}
            </div>
          </div>
          
          {/* Bottom call to action - shared between mobile and desktop */}
          <div className="text-center mt-8 mb-8">
            <div className="inline-flex flex-wrap justify-center items-center gap-4 bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20 rounded-full px-6 py-3">
              <span className="text-orange-400 font-medium">Private by default</span>
              <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
              <span className="text-orange-400 font-medium">Plug in external APIs</span>
              <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
              <span className="text-orange-400 font-medium">One workspace for every kind of work</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcaseSection;